import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NotifyService } from '@neutrino/core/notify';
import { BehaviorSubject, filter, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import EasyMde from 'easymde';
import logger from 'loglevel';

import { IMAGE_UPLOAD_EXT_MIME_TYPES } from '@flb/common/image-upload-ext-mimetypes';

import { imageUpload } from '../common/image-upload.js';
import { SimulatedLabelable } from '@neutrino/core/common';

import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/markdown-fold.js';
import CodeMirror, { EditorConfiguration } from 'codemirror';

const fte = CodeMirror.fromTextArea;
CodeMirror.fromTextArea = function (
    el: HTMLTextAreaElement,
    options?: EditorConfiguration | undefined
) {
    options = {
        ...(options || {}),
        gutters: ['CodeMirror-foldgutter'],
        foldGutter: true
    };
    return fte.call(CodeMirror, el, options);
};
// import { foldService } from 'codemirror';

// export const makeHeadingsFoldable = foldService.of((state, from, to) => {
//   const regex = /^ *(#{1,6}) /gm;
//   const match = regex.exec(state.doc.sliceString(from, to));
//   if (match === null || to === state.doc.length) {
//     return null;
//   }

//   // The heading level we are looking for
//   const level = match[1].length;
//   const lines = state.doc.lines;

//   for (let index = state.doc.lineAt(to).number; index <= lines; ++index) {
//     const line = state.doc.line(index);
//     const match = regex.exec(line.text);

//     // E.g., we can fold text between two "##" headings, or from "##" to "#",
//     // deeper headings like "###" will be collapsed.
//     if (match !== null && match[1].length <= level) {
//       return { from: to, to: line.from - 1 };
//     }
//   }

//   // The entire range after the current line can be collapsed
//   return { from: to, to: state.doc.length };
// });

@Component({
    selector: 'flb-mde',
    standalone: true,
    imports: [CommonModule, FormsModule],
    template: `
        <textarea #textarea></textarea>
        @for (pStream of progress | async; track pStream) {
            <ng-container *ngIf="pStream | async as p">
                <progress *ngIf="p != null" value="p"></progress>
            </ng-container>
        }
    `,
    styleUrl: './mde.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: MdeComponent,
            multi: true
        }
    ]
})
export class MdeComponent
    implements OnChanges, ControlValueAccessor, OnDestroy, SimulatedLabelable
{
    mde: EasyMde | null = null;

    // this must be set to a non-labelable element for SimulatedLabelable to work
    labelableElement: ElementRef<HTMLElement>;
    textareaRef = new BehaviorSubject<ElementRef<HTMLElement> | undefined>(undefined);

    @Input()
    foldSections: string[] = [];

    @ViewChild('textarea')
    set setTextarea(elemRef: ElementRef) {
        if (!this.mde) {
            this.textareaRef.next(elemRef);
            this.mde = new EasyMde({
                maxHeight: '40vh',
                element: elemRef.nativeElement,
                uploadImage: true,
                toolbar: [
                    'bold',
                    'italic',
                    'heading',
                    '|',
                    'quote',
                    'unordered-list',
                    'ordered-list',
                    '|',
                    'link',
                    'upload-image',
                    '|',
                    'preview',
                    'side-by-side',
                    'fullscreen',
                    '|',
                    'guide',
                    '|'
                ],
                previewImagesInEditor: true,
                imageAccept: Object.entries(IMAGE_UPLOAD_EXT_MIME_TYPES)
                    .map(kp => kp.join(','))
                    .join(','),
                imageUploadFunction: (file, onSuccess, onError) => {
                    this.uploadFile(file)
                        .then(onSuccess, err => onError(err.message))
                        .catch(logger.error);
                },
                errorCallback: msg => this.notify.error(msg)
            });
            this.mde.codemirror.on('change', () => {
                const value = this.mde?.value();
                if (value !== this.value) {
                    this.onChange(value);
                }
            });
            this.mde.codemirror.on('blur', () => this.onTouched());
        } else {
            if (this.textareaRef.value?.nativeElement !== elemRef.nativeElement) {
                throw new Error('Textarea replaced!?');
            }
        }
    }

    @Input()
    value = '';

    progress = new BehaviorSubject<Observable<number>[]>([]);

    private destroyed = new Subject<void>();

    constructor(
        elementRef: ElementRef,
        private httpClient: HttpClient,
        private notify: NotifyService
    ) {
        this.labelableElement = elementRef;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.foldSections) {
            this.doFold(changes.foldSections.currentValue);
        }
    }

    private doFold(foldSections: string[]) {
        const lines = this.value.split('\n');
        for (const fs of foldSections) {
            const line = lines.indexOf(fs);
            if (line) {
                this.mde?.codemirror.foldCode(line);
            }
        }
    }

    onLabelAttach(activateEvent: Observable<MouseEvent>) {
        activateEvent
            .pipe(takeUntil(this.destroyed))
            .forEach(() => {
                this.mde?.codemirror.focus();
            })
            .catch(logger.error);
    }

    ngOnDestroy(): void {
        this.mde?.cleanup();
        this.mde?.toTextArea();
        this.destroyed.next();
    }

    async uploadFile(f: File): Promise<string> {
        const extensions = Object.keys(IMAGE_UPLOAD_EXT_MIME_TYPES);
        let pObservable: Observable<number> | null = null;
        try {
            const progress = imageUpload(this.httpClient, extensions, f, 'user-image');
            pObservable = progress.pipe(filter(p => typeof p === 'number')) as Observable<number>;
            this.progress.next([...this.progress.value, pObservable]);
            return firstValueFrom(
                progress.pipe(filter(p => typeof p === 'string'))
            ) as Promise<string>;
        } finally {
            if (pObservable) {
                this.progress.next(this.progress.value.filter(p => p !== pObservable));
            }
        }
    }

    private onChange: (value: any) => void = (_: any) => {};
    public onTouched: () => void = () => {};

    writeValue(obj: any): void {
        this.value = obj || '';
        this.mde?.value(obj || '');
        if (this.foldSections) {
            this.doFold(this.foldSections);
        }
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.mde?.codemirror.setOption('readOnly', isDisabled);
    }
}
